import { z } from "zod";
import { CONTRACT_TYPES, CONTRACT_TYPE_LABEL, PROPERTY_TYPES, PROPERTY_TYPE_LABEL, type PropertyType, type ContractType } from "./portal.js";

// ============================================================================
// Buyer match alerts — a saved search + the matcher that fires on publish.
// ============================================================================

export const AlertCriteria = z.object({
  contract: z.enum(CONTRACT_TYPES).optional(),
  property_types: z.array(z.enum(PROPERTY_TYPES)).max(PROPERTY_TYPES.length).optional(),
  city: z.string().max(120).optional(),
  price_min: z.number().nonnegative().max(1_000_000_000).optional(),
  price_max: z.number().positive().max(1_000_000_000).optional(),
  mq_min: z.number().positive().max(100_000).optional(),
  rooms_min: z.number().int().positive().max(50).optional(),
}).refine((c) => c.price_min == null || c.price_max == null || c.price_min <= c.price_max, {
  message: "price_min must be <= price_max",
});
export type AlertCriteria = z.infer<typeof AlertCriteria>;

export const BuyerAlertInput = z.object({
  email: z.string().email().max(200),
  criteria: AlertCriteria,
  // Must accept the privacy notice; `alerts` is the explicit opt-in to be emailed.
  consents: z.object({ privacy: z.literal(true), alerts: z.literal(true) }),
  turnstile_token: z.string().min(10),
});
export type BuyerAlertInput = z.infer<typeof BuyerAlertInput>;

// Full lifecycle (display); only active|paused are agency-settable via PATCH.
export const ALERT_STATUS = ["pending", "active", "paused", "unsubscribed"] as const;
export type AlertStatus = (typeof ALERT_STATUS)[number];
export const ALERT_STATUS_SETTABLE = ["active", "paused"] as const;
export const AlertStatusPatchInput = z.object({ status: z.enum(ALERT_STATUS_SETTABLE) });
export type AlertStatusPatchInput = z.infer<typeof AlertStatusPatchInput>;

/** The listing fields a saved search is evaluated against. */
export type MatchableListing = {
  contract: string | null;
  property_type: string | null;
  city: string | null;
  prezzo: number | null;
  mq: number | null;
  rooms: number | null;
};

/**
 * Does a published listing satisfy a saved search? Conservative on price: a
 * reserved-price listing (prezzo null) never matches a price-bounded search,
 * because we can't confirm it's within budget.
 */
export function matchesAlert(criteria: AlertCriteria, l: MatchableListing): boolean {
  if (criteria.contract && l.contract !== criteria.contract) return false;
  if (criteria.property_types && criteria.property_types.length > 0) {
    if (!l.property_type || !criteria.property_types.includes(l.property_type as PropertyType)) return false;
  }
  if (criteria.city) {
    if (!l.city || !l.city.toLowerCase().includes(criteria.city.toLowerCase())) return false;
  }
  if (criteria.price_max != null && (l.prezzo == null || l.prezzo > criteria.price_max)) return false;
  if (criteria.price_min != null && (l.prezzo == null || l.prezzo < criteria.price_min)) return false;
  if (criteria.mq_min != null && (l.mq == null || l.mq < criteria.mq_min)) return false;
  if (criteria.rooms_min != null && (l.rooms == null || l.rooms < criteria.rooms_min)) return false;
  return true;
}

/** Human one-line summary of a saved search (admin list + emails). */
export function criteriaSummary(c: AlertCriteria): string {
  const parts: string[] = [];
  if (c.contract) parts.push(CONTRACT_TYPE_LABEL[c.contract as ContractType] ?? c.contract);
  if (c.property_types && c.property_types.length > 0) parts.push(c.property_types.map((t) => PROPERTY_TYPE_LABEL[t]).join("/"));
  if (c.city) parts.push(c.city);
  if (c.price_min != null || c.price_max != null) {
    parts.push(`€${c.price_min != null ? c.price_min.toLocaleString("it-IT") : "0"}–${c.price_max != null ? c.price_max.toLocaleString("it-IT") : "∞"}`);
  }
  if (c.mq_min != null) parts.push(`≥${c.mq_min}mq`);
  if (c.rooms_min != null) parts.push(`≥${c.rooms_min} locali`);
  return parts.length > 0 ? parts.join(" · ") : "qualsiasi immobile";
}
